// What the table publishes (onStateChange) and how. Every committed change publishes one complete,
// JSON-serializable replacement state with its reason; the first render, drafts, expansion and consumer
// (controlled) updates publish nothing.

import { refinementOn, enumSelection } from '../query/filters'
import { visibleKeys } from '../query/export'
import { viewKeys } from '../view/view'

// The kind of change a reason belongs to.
export const CHANGE_OF = {
  search: 'query',
  filter: 'query',
  sort: 'query',
  reset: 'query',
  page: 'page',
  pageSize: 'pageSize',
  view: 'view',
  selection: 'selection',
  expansion: 'expansion',
  response: 'response',
  controlled: 'controlled',
}

// Enum filters publish their selection, plus `refined: true` while the refinement is on.
export const publishedFilters = filters => {
  const out = {}
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (Array.isArray(value) || (value && Array.isArray(value.selected))) {
      const selected = enumSelection(value).slice()
      out[key] = refinementOn(value) ? { selected, refined: true } : selected
    } else {
      out[key] = value && { ...value }
    }
  })
  return out
}

// Page-scoped selection publishes its row keys; query-scoped selection publishes its query and exact
// eligible count, never every matching key.
export const publishedSelection = (selection, query, eligibleTotal) => {
  if (!selection || selection.scope === 'none') return { scope: 'none' }
  if (selection.scope === 'all')
    return {
      scope: 'all',
      query: {
        search: query.search,
        filters: publishedFilters(query.filters),
      },
      eligibleTotal: Number.isInteger(eligibleTotal) ? eligibleTotal : null,
      excluded: Array.from(selection.excluded || []),
    }
  return { scope: selection.scope, ids: Array.from(selection.ids || []) }
}

/**
 * The complete published state.
 * @param {import('../types').TableState} state
 * @param {import('../types').NormalizedConfig} config
 * @param {{eligibleTotal?: number}} [extra] the selection count
 */
export const publishedState = (state, config, extra = {}) => {
  const columns = config.columns || []
  // the rendered order: locked columns at their edges, as the View places them
  const keys = state.view
    ? viewKeys(columns, state.view)
    : visibleKeys(columns, state)
  // a hidden column keeps its width, so showing it again after a restore brings back the width it had
  const known = columns.map(column => column.key)
  const widths = {}
  Object.entries((state.view && state.view.widths) || {}).forEach(
    ([key, width]) => {
      if (known.includes(key)) widths[key] = width
    }
  )
  const published = {
    tableId: config.tableId,
    columns: keys,
    hidden: known.filter(key => !keys.includes(key)),
    widths,
    density: state.view ? state.view.density : config.defaultDensity,
    search: state.query.search,
    filters: publishedFilters(state.query.filters),
    sort: { ...state.query.sort },
    page: state.page,
    pageSize: state.pageSize,
  }
  if (config.selection)
    published.selection = publishedSelection(
      state.selection,
      state.query,
      extra.eligibleTotal
    )
  return published
}

export const eventOf = reason => ({
  reason: reason || null,
  change: CHANGE_OF[reason] || null,
})
